import { Popover, PopoverInteractionKind } from '@blueprintjs/core'
import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import ReactMarkdown from 'react-markdown'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { translateString } from 'shared-libs/helpers/utils'
import type { LabelProps } from 'shared-libs/components/ui2/fields/field-ui/label/label-props'
import { NO_LABEL } from 'shared-libs/components/ui2/fields/field-ui/label/label-props'
import { UI2FormContext } from 'shared-libs/components/ui2/forms/ui2-form/ui2-form-context'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import './_label.scss'

interface IUI2LabelProps extends LabelProps, Omit<IBaseProps, 'children'> {
  frames?: FramesManager
  translationTable?: any
  textStyleClassName?: string
}

interface IUI2LabelState {
  isTruncated: boolean
}

export class UI2Label extends React.PureComponent<IUI2LabelProps, IUI2LabelState> {
  public static contextType = UI2FormContext
  public declare context: React.ContextType<typeof UI2FormContext>
  public static defaultProps: Partial<IUI2LabelProps> = { maxLines: 2 }

  public state: IUI2LabelState = { isTruncated: false }

  private textRef = React.createRef<HTMLSpanElement>()
  private resizeObserver?: ResizeObserver

  public componentDidMount() {
    this.measureTruncation()
    if (typeof ResizeObserver !== 'undefined' && this.textRef.current) {
      this.resizeObserver = new ResizeObserver(() => this.measureTruncation())
      this.resizeObserver.observe(this.textRef.current)
    }
  }

  public componentDidUpdate(prevProps: IUI2LabelProps) {
    if (
      prevProps.labelText !== this.props.labelText ||
      prevProps.maxLines !== this.props.maxLines ||
      prevProps.translationTable !== this.props.translationTable
    ) {
      this.measureTruncation()
    }
  }

  public componentWillUnmount() {
    this.resizeObserver?.disconnect()
  }

  private measureTruncation = () => {
    const el = this.textRef.current
    if (!el) return
    const isTruncated = el.scrollHeight - el.clientHeight > 1
    if (isTruncated !== this.state.isTruncated) {
      this.setState({ isTruncated })
    }
  }

  public render() {
    const { labelText } = this.props
    if (_.isEmpty(labelText) || labelText === NO_LABEL) return null
    return (
      <div className={classNames('ui2-label', this.props.className)}>
        <div className='ui2-label-row' data-testid={labelText + '_Label'}>
          <div className='ui2-label-content'>
            {this.renderText()}
            {this.renderRequiredIndicator()}
          </div>
          {this.renderTooltipButton()}
        </div>
        {this.renderHelpText()}
      </div>
    )
  }

  private renderText() {
    const { labelText, maxLines, translationTable } = this.props
    const translated = translateString(labelText!, translationTable || this.getTranslationTable())
    const clampStyle: React.CSSProperties = {
      WebkitLineClamp: maxLines,
      ...(maxLines && maxLines > 0
        ? { display: '-webkit-box', WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }
        : null),
    }
    const span = (
      <span
        ref={this.textRef}
        className={classNames('ui2-label-text', this.props.textStyleClassName)}
        style={clampStyle}
      >
        {translated}
      </span>
    )
    if (!this.state.isTruncated) return span
    return (
      <Popover
        interactionKind={PopoverInteractionKind.HOVER}
        hoverOpenDelay={150}
        content={<div className='ui2-label-tooltip'>{translated}</div>}
      >
        {span}
      </Popover>
    )
  }

  private renderRequiredIndicator() {
    const { isRequired, isOptional } = this.props
    if (!isRequired && !isOptional) return null
    return (
      <span className={classNames('ui2-label-requiredType', {
        'ui2-label-requiredType--required': isRequired,
        'ui2-label-requiredType--optional': isOptional && !isRequired,
      })}>
        {isRequired ? ' *' : ' (optional)'}
      </span>
    )
  }

  private renderHelpText() {
    if (this.context?.inlineHelpText === null) return null
    const { inlineHelpText } = this.props
    const translationTable = this.props.translationTable || this.getTranslationTable()
    if (_.isEmpty(inlineHelpText)) return null
    const translated = translateString(inlineHelpText, translationTable) ?? inlineHelpText
    return (
      <div className='ui2-label-helpText'>
        <ReactMarkdown>{translated}</ReactMarkdown>
      </div>
    )
  }

  private renderTooltipButton() {
    const { labelText, tooltipText } = this.props
    const translationTable = this.props.translationTable || this.getTranslationTable()
    if (_.isEmpty(tooltipText)) return null
    const translated = translateString(tooltipText, translationTable) ?? tooltipText
    return (
      <Popover
        interactionKind={PopoverInteractionKind.CLICK}
        content={<div className='ui2-label-tooltip'><ReactMarkdown>{translated}</ReactMarkdown></div>}
      >
        <button
          aria-label={'More information about ' + (labelText || '')}
          className='ui2-label-tooltipButton'
          data-testid={labelText + '_Tooltip'}
          type='button'
        >
          <UI2Icon name={UI2IconNames.INFO} size={14} />
        </button>
      </Popover>
    )
  }

  private getTranslationTable() {
    return this.props.frames?.getContext('translationTable')
  }
}
